import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Divider,
  Link,
  useColorModeValue,
} from "@chakra-ui/react";
import Footer from "../../components/Footer";

const Section = ({ title, children }) => (
  <VStack align="start" spacing={3} w="full">
    <Heading as="h2" size="md" color="gray.900" fontWeight="600">
      {title}
    </Heading>
    {children}
  </VStack>
);

const PrivacyPolicy = () => {
  const bgColor = useColorModeValue("gray.50", "gray.800");
  const lastUpdated = "March 1, 2026";

  return (
    <Box bg="white" minH="100vh">
      {/* Header */}
      <Box bg={bgColor} py={{ base: "3rem", md: "4rem" }}>
        <Container maxW="4xl" px={{ base: 4, md: 8 }}>
          <VStack spacing="1rem" textAlign="center">
            <Heading as="h1" size="xl" color="gray.900" fontWeight="700">
              Privacy Policy
            </Heading>
            <Text fontSize="md" color="gray.600">
              Last updated: {lastUpdated}
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxW="4xl" px={{ base: 4, md: 8 }} py={{ base: "2rem", md: "3rem" }}>
        <VStack spacing={8} align="start">
          
          <Text color="gray.700" lineHeight="1.8">
            SkillBack ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our web application and Chrome extension.
          </Text>

          <Divider />

          <Section title="1. Information We Collect">
            <Text color="gray.700" lineHeight="1.8">
              We collect information that you voluntarily provide to us when you create an account and use our services:
            </Text>
            <UnorderedList spacing={2} pl={4} color="gray.700">
              <ListItem><strong>Account Information:</strong> Email address, name, and password when you sign up.</ListItem>
              <ListItem><strong>Profile Data:</strong> Professional information you enter, including work history, job titles, companies, dates of employment, and achievements.</ListItem>
              <ListItem><strong>Application Data:</strong> Job descriptions, target roles, company names, and tailored resume content you create.</ListItem>
              <ListItem><strong>Subscription Information:</strong> Subscription status for premium features.</ListItem>
            </UnorderedList>
          </Section>

          <Divider />

          <Section title="2. How We Use Your Information">
            <Text color="gray.700" lineHeight="1.8">
              We use your information solely to provide and improve our services:
            </Text>
            <UnorderedList spacing={2} pl={4} color="gray.700">
              <ListItem><strong>Resume Generation:</strong> Your work history and achievements are used to generate tailored resume content using AI.</ListItem>
              <ListItem><strong>AI-Powered Features:</strong> We use large language models (LLMs) to generate achievement bullets, professional summaries, and answer career-related questions based on your profile data.</ListItem>
              <ListItem><strong>Job Matching:</strong> Your skills and experience are analyzed to provide match scores and recommendations for job applications.</ListItem>
              <ListItem><strong>Service Improvement:</strong> Aggregated, anonymized data may be used to improve our AI models and user experience.</ListItem>
            </UnorderedList>
          </Section>

          <Divider />

          <Section title="3. Chrome Extension">
            <Text color="gray.700" lineHeight="1.8">
              Our Chrome extension is designed to help you use your SkillBack data more efficiently:
            </Text>
            <UnorderedList spacing={2} pl={4} color="gray.700">
              <ListItem>The extension only works for authenticated SkillBack subscribers.</ListItem>
              <ListItem>It allows you to copy and use your own data that you have already provided to SkillBack.</ListItem>
              <ListItem>We do not scrape, collect, or read any data from websites you visit.</ListItem>
              <ListItem>The extension does not track your browsing activity or collect any data from third-party websites.</ListItem>
            </UnorderedList>
          </Section>

          <Divider />

          <Section title="4. Data Storage and Security">
            <Text color="gray.700" lineHeight="1.8">
              We take the security of your data seriously:
            </Text>
            <UnorderedList spacing={2} pl={4} color="gray.700">
              <ListItem>Your data is stored securely using industry-standard encryption.</ListItem>
              <ListItem>We use Supabase for secure database hosting with row-level security policies.</ListItem>
              <ListItem>All data transmission is encrypted using HTTPS/TLS.</ListItem>
              <ListItem>We do not sell, rent, or share your personal data with third parties for marketing purposes.</ListItem>
            </UnorderedList>
          </Section>

          <Divider />

          <Section title="5. Third-Party Services">
            <Text color="gray.700" lineHeight="1.8">
              We use the following third-party services to operate SkillBack:
            </Text>
            <UnorderedList spacing={2} pl={4} color="gray.700">
              <ListItem><strong>Supabase:</strong> Database and authentication services.</ListItem>
              <ListItem><strong>Azure OpenAI:</strong> AI language models for content generation. Your data is processed according to Microsoft's enterprise data protection policies and is not used to train their models.</ListItem>
            </UnorderedList>
          </Section>

          <Divider />

          <Section title="6. Your Rights">
            <Text color="gray.700" lineHeight="1.8">
              You have the following rights regarding your data:
            </Text>
            <UnorderedList spacing={2} pl={4} color="gray.700">
              <ListItem><strong>Access:</strong> You can view all your data within your SkillBack account at any time.</ListItem>
              <ListItem><strong>Correction:</strong> You can edit or update your information directly in the application.</ListItem>
              <ListItem><strong>Deletion:</strong> You can request deletion of your account and all associated data by contacting us.</ListItem>
              <ListItem><strong>Export:</strong> You can download your resume and profile data from the application.</ListItem>
            </UnorderedList>
          </Section>

          <Divider />

          <Section title="7. Data Retention">
            <Text color="gray.700" lineHeight="1.8">
              We retain your data for as long as your account is active. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it for legal or regulatory purposes.
            </Text>
          </Section>

          <Divider />

          <Section title="8. Children's Privacy">
            <Text color="gray.700" lineHeight="1.8">
              SkillBack is not intended for users under 16 years of age. We do not knowingly collect personal information from children under 16.
            </Text>
          </Section>

          <Divider />

          <Section title="9. Changes to This Policy">
            <Text color="gray.700" lineHeight="1.8">
              We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on this page and updating the "Last updated" date.
            </Text>
          </Section>

          <Divider />

          <Section title="10. Contact Us">
            <Text color="gray.700" lineHeight="1.8">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </Text>
            <Text color="gray.700" lineHeight="1.8">
              Email: <Link href="mailto:support@skillback.io" color="blue.600">support@skillback.io</Link>
            </Text>
          </Section>

        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
